import React, { useState, useEffect } from 'react';
import { userManagementAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './MasterDataManagement.css';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const UserManagement = () => {
  const { t } = useTranslation(['userManagement', 'common']);
  const { user } = useAuth(); // Get current logged-in user
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedPpdForModal, setSelectedPpdForModal] = useState(''); // PPD filter for modal school dropdown
  const [newUser, setNewUser] = useState({
    id: 0,
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'SCHOOL',
    schoolCode: '',
    ppd: '',
    dunCode: '',
    parCode: '',
    isActive: true,
    isSuperuser: false
  });
  const [passwordResetData, setPasswordResetData] = useState({
    userId: 0,
    userName: '',
    userEmail: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Permissions and dropdowns
  const [permissions, setPermissions] = useState({
    canAdd: false,
    canEdit: false,
    canDelete: false,
    dataScope: 'NONE',
    role: ''
  });
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Load permissions and dropdowns on mount
  useEffect(() => {
    loadPermissions();
    loadDropdowns();
  }, []);

  // Load users when filters change
  useEffect(() => {
    loadUsers();
  }, [searchTerm, roleFilter, statusFilter, districtFilter, schoolFilter, currentPage, pageSize]);

  // Load schools when district changes
  useEffect(() => {
    if (districtFilter) {
      loadSchools(districtFilter);
    } else if (permissions.dataScope === 'ALL') {
      // ADMIN/SUPERADMIN: reload all schools when district filter is cleared
      loadSchools();
    }
  }, [districtFilter]);

  const loadPermissions = async () => {
    try {
      const response = await userManagementAPI.getPermissions();
      if (response.success) {
        setPermissions({
          canAdd: response.data.canAdd || false,
          canEdit: response.data.canEdit || false,
          canDelete: response.data.canDelete || false,
          dataScope: response.data.dataScope || 'NONE',
          role: response.data.role || ''
        });
      }
    } catch (err) {
      console.error('Load permissions error:', err);
    }
  };

  const loadDropdowns = async () => {
    try {
      // Load districts for ADMIN/SUPERADMIN only (PPD users don't need district dropdown)
      const permResponse = await userManagementAPI.getPermissions();
      if (permResponse.success) {
        const role = permResponse.data.role;

        // Load districts for ADMIN, SUPERADMIN only
        if (role === 'ADMIN' || role === 'SUPERADMIN') {
          try {
            const districtsResponse = await userManagementAPI.getDistricts();
            if (districtsResponse.success) {
              setDistricts(districtsResponse.data || []);
            }
          } catch (districtErr) {
            // Ignore 403 errors - user doesn't have access to districts endpoint
            console.log('Districts not available for this role');
          }
        }
      }

      // Load schools (role-based filtering done by backend)
      const schoolsResponse = await userManagementAPI.getSchools();
      if (schoolsResponse.success) {
        setSchools(schoolsResponse.data || []);
      }
    } catch (err) {
      console.error('Load dropdowns error:', err);
    }
  };

  const loadSchools = async (district = null) => {
    try {
      const response = await userManagementAPI.getSchools(district);
      if (response.success) {
        setSchools(response.data || []);
      }
    } catch (err) {
      console.error('Load schools error:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await userManagementAPI.getUsers(
        searchTerm,
        roleFilter,
        statusFilter,
        districtFilter,
        schoolFilter
      );

      if (response.success) {
        setUsers(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Load users error:', err);
      setError('An error occurred while loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setEditingUser(null);

    // Set default role based on current user's role
    let defaultRole = 'TEACHER';
    let defaultSchoolCode = '';
    let defaultPpdForModal = '';

    if (permissions.role === 'HEADMASTER') {
      defaultRole = 'TEACHER';
      // HEADMASTER: Set schoolCode to their school (cannot change)
      defaultSchoolCode = user?.schoolCode || '';
    } else if (permissions.role === 'PPD') {
      defaultRole = 'HEADMASTER';
      // PPD: Pre-select their district in PPD filter and ppd field
      defaultPpdForModal = user?.ppd || '';
    } else if (permissions.role === 'ADMIN' || permissions.role === 'SUPERADMIN') {
      defaultRole = 'PPD';
      // ADMIN/SUPERADMIN: No pre-selection
    }

    setNewUser({
      id: 0,
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: defaultRole,
      schoolCode: defaultSchoolCode,
      ppd: defaultPpdForModal,  // Set PPD for PPD users
      dunCode: '',
      parCode: '',
      isActive: true,
      isSuperuser: false
    });

    setSelectedPpdForModal(defaultPpdForModal);

    // Load schools filtered by PPD if PPD is pre-selected
    if (defaultPpdForModal) {
      loadSchools(defaultPpdForModal);
    }

    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setEditingUser(user);
    setNewUser({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      role: user.role || 'SCHOOL',
      schoolCode: user.schoolCode || '',
      ppd: user.ppd || '',
      dunCode: user.dunCode || '',
      parCode: user.parCode || '',
      isActive: user.isActive,
      isSuperuser: user.isSuperuser
    });

    // Set PPD for modal and load schools for that district
    if (user.ppd) {
      setSelectedPpdForModal(user.ppd);
      loadSchools(user.ppd);
    }

    setShowModal(true);
  };

  const handleDeleteUser = async (user) => {
    const confirmMessage = t('userManagement:messages.confirm.delete', { name: user.name, email: user.email });

    if (window.confirm(confirmMessage)) {
      try {
        const response = await userManagementAPI.deleteUser(user.id);

        if (response.success) {
          loadUsers();
          setSuccessMessage(t('userManagement:messages.success.deleted'));
          console.log('User deleted successfully');
        } else {
          setError(response.message || 'Failed to delete user');
        }
      } catch (err) {
        console.error('Delete error:', err);
        // Extract specific error message from API response
        const errorMessage = err?.response?.data?.message ||
                           err?.response?.data?.errors?.[0] ||
                           err?.message ||
                           'An error occurred while deleting the user';
        setError(errorMessage);
      }
    }
  };

  const handleResetPassword = (user) => {
    setPasswordResetData({
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handleSaveUser = async () => {
    try {
      // Clear previous messages
      setError('');
      setSuccessMessage('');

      // Password regex: at least one uppercase, one lowercase, and one number, minimum 8 characters
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

      // Validate password complexity for create mode
      if (modalMode === 'create') {
        if (newUser.password && newUser.password.length < 8) {
          setError(t('userManagement:messages.error.passwordMinLength'));
          return;
        }
        if (newUser.password && !passwordRegex.test(newUser.password)) {
          setError(t('userManagement:messages.error.passwordComplexity'));
          return;
        }
        if (newUser.password !== newUser.confirmPassword) {
          setError(t('userManagement:messages.error.passwordMismatch'));
          return;
        }
      }

      // Validate password complexity for edit mode (if password is being changed)
      if (modalMode === 'edit' && newUser.password) {
        if (newUser.password.length < 8) {
          setError(t('userManagement:messages.error.passwordMinLength'));
          return;
        }
        if (!passwordRegex.test(newUser.password)) {
          setError(t('userManagement:messages.error.passwordComplexity'));
          return;
        }
        if (newUser.password !== newUser.confirmPassword) {
          setError(t('userManagement:messages.error.passwordMismatch'));
          return;
        }
      }

      let response;

      if (modalMode === 'create') {
        response = await userManagementAPI.createUser({
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          role: newUser.role,
          schoolCode: newUser.schoolCode,
          ppd: newUser.ppd,
          dunCode: newUser.dunCode,
          parCode: newUser.parCode,
          isActive: newUser.isActive,
          isSuperuser: newUser.isSuperuser
        });
      } else {
        const updatePayload = {
          id: newUser.id,
          name: newUser.name,
          email: newUser.email,
          role: newUser.role,
          schoolCode: newUser.schoolCode,
          ppd: newUser.ppd,
          dunCode: newUser.dunCode,
          parCode: newUser.parCode,
          isActive: newUser.isActive,
          isSuperuser: newUser.isSuperuser
          // Password changes are handled via separate "Reset Password" functionality
        };

        console.log('Update payload being sent:', updatePayload);
        response = await userManagementAPI.updateUser(editingUser.id, updatePayload);
      }

      if (response.success) {
        setShowModal(false);
        setNewUser({
          id: 0,
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'SCHOOL',
          schoolCode: '',
          ppd: '',
          dunCode: '',
          parCode: '',
          isActive: true,
          isSuperuser: false
        });
        loadUsers();

        // Show success message
        Swal.fire({
          icon: 'success',
          title: modalMode === 'create' ? t('userManagement:messages.success.created') : t('userManagement:messages.success.updated'),
          confirmButtonText: 'OK'
        });

        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} user successfully`);
      } else {
        // Show user-friendly error for duplicate email or other errors
        console.error('API returned error:', response);
        setError(response.message || t(`userManagement:messages.error.${modalMode === 'create' ? 'createFailed' : 'updateFailed'}`));
      }
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err?.response?.data);

      // Extract detailed error message from API response
      const errorMessage = err?.response?.data?.message ||
                         err?.response?.data?.errors?.[0] ||
                         err?.message ||
                         t(`userManagement:messages.error.${modalMode === 'create' ? 'createFailed' : 'updateFailed'}`);

      setError(errorMessage);
    }
  };

  const handleSavePasswordReset = async () => {
    // Clear previous messages
    setError('');
    setSuccessMessage('');

    if (!passwordResetData.newPassword || passwordResetData.newPassword.length < 8) {
      setError(t('userManagement:messages.error.passwordMinLength'));
      return;
    }

    // Validate password complexity: at least one uppercase, one lowercase, and one number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(passwordResetData.newPassword)) {
      setError(t('userManagement:messages.error.passwordComplexity'));
      return;
    }

    // Validate password confirmation
    if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
      setError(t('userManagement:messages.error.passwordMismatch'));
      return;
    }

    try {
      const response = await userManagementAPI.resetUserPassword(passwordResetData.userId, {
        userId: passwordResetData.userId,
        newPassword: passwordResetData.newPassword
      });

      if (response.success) {
        const userName = passwordResetData.userName;
        const userEmail = passwordResetData.userEmail;

        setShowPasswordModal(false);
        setPasswordResetData({
          userId: 0,
          userName: '',
          userEmail: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSuccessMessage(`${t('userManagement:messages.success.passwordReset')} for ${userName} (${userEmail})`);

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(response.message || t('userManagement:messages.error.passwordResetFailed'));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t('userManagement:messages.error.passwordResetFailed'));
    }
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('common:capturingScreenshot'),
        html: t('common:pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('userManagement:messages.error.captureFailed'),
        text: t('userManagement:messages.error.captureFailedMessage')
      });
    }
  };

  if (loading && !users.length) {
    return (
      <div className="master-data-management">
        <div className="loading">{t('userManagement:loading')}</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>{t('userManagement:title')} ({t('userManagement:titleEn')})</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('userManagement:tooltips.reportIssue')}>
          <i className="fas fa-bug"></i>
        </button>
      </div>
      <div className="master-data-management-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', display: 'none' }}>
        <div className="header-info">
          <h2><i className="fas fa-users"></i> {t('userManagement:title')}</h2>
          <p>{t('userManagement:subtitle')}</p>
        </div>
        <button
          className="btn btn-report-issue"
          onClick={handleReportIssueClick}
          title={t('userManagement:tooltips.reportIssue')}
          style={{
            padding: '8px 16px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-bug"></i>
          {t('userManagement:buttons.reportIssue')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('userManagement:search.placeholder')}
            className="search-input"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('userManagement:filters.allRoles')}</option>
            <option value="ADMIN">{t('userManagement:filters.roles.ADMIN')}</option>
            <option value="PPD">{t('userManagement:filters.roles.PPD')}</option>
            <option value="HEADMASTER">{t('userManagement:filters.roles.HEADMASTER')}</option>
            <option value="TEACHER">{t('userManagement:filters.roles.TEACHER')}</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('userManagement:filters.allStatuses')}</option>
            <option value="active">{t('userManagement:filters.statuses.active')}</option>
            <option value="inactive">{t('userManagement:filters.statuses.inactive')}</option>
          </select>

          {/* District filter - only show for ADMIN/SUPERADMIN */}
          {permissions.dataScope === 'ALL' && districts.length > 0 && (
            <select
              value={districtFilter}
              onChange={(e) => {
                setDistrictFilter(e.target.value);
                setSchoolFilter(''); // Clear school filter when district changes
              }}
              className="filter-select"
            >
              <option value="">{t('userManagement:filters.allDistricts')}</option>
              {districts.map((district, index) => (
                <option key={index} value={district}>{district}</option>
              ))}
            </select>
          )}

          {/* School filter - show for all roles (backend filters based on role) */}
          {schools.length > 0 && (
            <select
              value={schoolFilter}
              onChange={(e) => setSchoolFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('userManagement:filters.allSchools')}</option>
              {schools.map((school) => (
                <option key={school.schoolCode} value={school.schoolCode}>
                  {school.schoolCode} - {school.name}
                </option>
              ))}
            </select>
          )}

          <button onClick={loadUsers} className="btn-search">
            <i className="fas fa-filter"></i> {t('userManagement:search.filterButton')}
          </button>
        </div>

        {/* Only show Add button if user has CanAdd permission */}
        {permissions.canAdd && (
          <button onClick={handleCreateUser} className="btn-create">
            <i className="fas fa-plus"></i> {t('userManagement:buttons.addNewUser')}
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>{t('userManagement:table.headers.no')}</th>
              <th>{t('userManagement:table.headers.name')}</th>
              <th>{t('userManagement:table.headers.email')}</th>
              <th>{t('userManagement:table.headers.role')}</th>
              <th>{t('userManagement:table.headers.schoolCode')}</th>
              <th>{t('userManagement:table.headers.ppd')}</th>
              <th>{t('userManagement:table.headers.dun')}</th>
              <th>{t('userManagement:table.headers.parliament')}</th>
              <th>{t('userManagement:table.headers.status')}</th>
              <th>{t('userManagement:table.headers.createdDate')}</th>
              <th>{t('userManagement:table.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr key={user.id}>
                  <td>{index + 1 + (currentPage - 1) * pageSize}</td>
                  <td><strong>{user.name}</strong></td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${
                      user.role?.toUpperCase() === 'SUPERADMIN' ? 'badge-superadmin' : 
                      user.role?.toUpperCase() === 'ADMIN' ? 'badge-admin' : 
                      user.role?.toUpperCase() === 'PPD' ? 'badge-ppd' : 
                      user.role?.toUpperCase() === 'KTC' ? 'badge-ktc' : 
                      user.role?.toUpperCase() === 'HEADMASTER' ? 'badge-headmaster' : 
                      user.role?.toUpperCase() === 'TEACHER' ? 'badge-teacher' : 
                      user.role?.toUpperCase() === 'SCHOOL' ? 'badge-school' : 
                      'badge-secondary'
                    }`}>
                      {user.role || 'N/A'}
                    </span>
                  </td>
                  <td>{user.schoolCode || '-'}</td>
                  <td>{user.ppd || '-'}</td>
                  <td>{user.dunCode || '-'}</td>
                  <td>{user.parCode || '-'}</td>
                  <td>
                    <span className={`badge ${user.isActive ? 'badge-success' : 'badge-secondary'}`}>
                      {user.isActive ? t('userManagement:table.status.active') : t('userManagement:table.status.inactive')}
                    </span>
                  </td>
                  <td>
                    {user.createdDate ? new Date(user.createdDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="actions-column">
                    {permissions.canEdit && (
                      <button
                        onClick={() => handleEditUser(user)}
                        className="btn-action btn-edit"
                        title={t('userManagement:tooltips.editUser')}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                    {permissions.canEdit && (
                      <button
                        onClick={() => handleResetPassword(user)}
                        className="btn-action btn-password"
                        title={t('userManagement:tooltips.resetPassword')}
                      >
                        <i className="fas fa-key"></i>
                      </button>
                    )}
                    {permissions.canDelete && (
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="btn-action btn-delete"
                        title={t('userManagement:tooltips.deleteUser')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="no-data">{t('userManagement:table.noData')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            {t('userManagement:pagination.page')} {currentPage} {t('userManagement:pagination.of')} {totalPages} ({totalCount} {t('userManagement:pagination.totalUsers')})
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              {t('userManagement:pagination.first')}
            </button>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              {t('userManagement:pagination.previous')}
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>{t('userManagement:pagination.of')} {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              {t('userManagement:pagination.next')}
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              {t('userManagement:pagination.last')}
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 {t('userManagement:pagination.perPage')}</option>
            <option value={50}>50 {t('userManagement:pagination.perPage')}</option>
            <option value={100}>100 {t('userManagement:pagination.perPage')}</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit User */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? t('userManagement:modal.add.title') : `${t('userManagement:modal.edit.title')} ${newUser.name}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Error and Success Messages inside Modal */}
              {error && <div className="error-message" style={{ marginBottom: '15px' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ marginBottom: '15px' }}>{successMessage}</div>}

              <div className="form-section">
                <h4><i className="fas fa-user"></i> {t('userManagement:modal.sections.personalInfo')}</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="userName">{t('userManagement:modal.fields.fullName.label')} *</label>
                    <input
                      id="userName"
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      placeholder={t('userManagement:modal.fields.fullName.placeholder')}
                      maxLength={255}
                      className="form-control"
                    />
                    <small className="form-help">{t('userManagement:modal.fields.fullName.help')}</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="userEmail">{t('userManagement:modal.fields.email.label')} *</label>
                    <input
                      id="userEmail"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value.trim().toLowerCase() })}
                      placeholder={t('userManagement:modal.fields.email.placeholder')}
                      maxLength={255}
                      className="form-control"
                    />
                    <small className="form-help">{t('userManagement:modal.fields.email.help')}</small>
                  </div>
                </div>

                {modalMode === 'create' && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="userPassword">{t('userManagement:modal.fields.password.label')} *</label>
                        <input
                          id="userPassword"
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder={t('userManagement:modal.fields.password.placeholder')}
                          minLength={6}
                          className="form-control"
                        />
                        <small className="form-help">{t('userManagement:modal.fields.password.help')}</small>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="userConfirmPassword">{t('userManagement:modal.fields.confirmPassword.label')} *</label>
                        <input
                          id="userConfirmPassword"
                          type="password"
                          value={newUser.confirmPassword}
                          onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                          placeholder={t('userManagement:modal.fields.confirmPassword.placeholder')}
                          minLength={6}
                          className="form-control"
                        />
                        <small className="form-help">{t('userManagement:modal.fields.confirmPassword.help')}</small>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="form-section">
                <h4><i className="fas fa-id-badge"></i> {t('userManagement:modal.sections.roleAssignment')}</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="userRole">{t('userManagement:modal.fields.role.label')} *</label>

                    {/* In EDIT mode: Show role as read-only (cannot change role after creation) */}
                    {modalMode === 'edit' ? (
                      <input
                        id="userRole"
                        type="text"
                        value={newUser.role}
                        readOnly
                        disabled
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed', fontWeight: 'bold' }}
                      />
                    ) : (
                      /* In CREATE mode: Show dropdown based on permissions */
                      <select
                        id="userRole"
                        value={newUser.role}
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value.toUpperCase() })}
                        className="form-control"
                      >
                        {/* HEADMASTER can only create TEACHER */}
                        {permissions.role === 'HEADMASTER' && (
                          <option value="TEACHER">TEACHER</option>
                        )}

                        {/* PPD can only create HEADMASTER */}
                        {permissions.role === 'PPD' && (
                          <option value="HEADMASTER">HEADMASTER</option>
                        )}

                        {/* ADMIN/SUPERADMIN can create PPD, HEADMASTER, TEACHER */}
                        {(permissions.role === 'ADMIN' || permissions.role === 'SUPERADMIN') && (
                          <>
                            <option value="PPD">PPD</option>
                            <option value="HEADMASTER">HEADMASTER</option>
                            <option value="TEACHER">TEACHER</option>
                          </>
                        )}
                      </select>
                    )}
                    <small className="form-help">
                      {modalMode === 'edit'
                        ? 'Role cannot be changed after user creation'
                        : t('userManagement:modal.fields.ppd.help')}
                    </small>
                  </div>

                </div>

                {/* PPD Field - Read-only for PPD users in edit mode, Dropdown for ADMIN/SUPERADMIN */}
                {(permissions.role === 'PPD' || permissions.role === 'ADMIN' || permissions.role === 'SUPERADMIN') && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="ppdFilter">{t('userManagement:modal.fields.ppd.label')} *</label>

                      {/* PPD users: Show current PPD as read-only in edit mode */}
                      {permissions.role === 'PPD' ? (
                        <input
                          id="ppdFilter"
                          type="text"
                          value={newUser.ppd || permissions.ppd || ''}
                          readOnly
                          disabled
                          className="form-control"
                          style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                        />
                      ) : (
                        /* ADMIN/SUPERADMIN: Show dropdown */
                        <select
                          id="ppdFilter"
                          value={selectedPpdForModal}
                          onChange={(e) => {
                            setSelectedPpdForModal(e.target.value);
                            loadSchools(e.target.value);
                            setNewUser({ ...newUser, ppd: e.target.value, schoolCode: '' }); // Update ppd and reset school
                          }}
                          className="form-control"
                        >
                          <option value="">{t('userManagement:modal.fields.ppd.placeholder')}</option>
                          {districts.map((district) => (
                            <option key={district} value={district}>
                              {district}
                            </option>
                          ))}
                        </select>
                      )}
                      <small className="form-help">
                        {permissions.role === 'PPD'
                          ? 'PPD users can only manage users in their own district'
                          : t('userManagement:modal.fields.school.selectDistrict')}
                      </small>
                    </div>
                  </div>
                )}

                {/* School Code Field */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="schoolCode">
                      {t('userManagement:modal.fields.school.label')} {permissions.role === 'HEADMASTER' ? '(Your School)' : '*'}
                    </label>

                    {/* EDIT mode: School is read-only (cannot change school after user creation) */}
                    {modalMode === 'edit' ? (
                      <input
                        id="schoolCode"
                        type="text"
                        value={newUser.schoolCode}
                        readOnly
                        disabled
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed', fontWeight: 'bold' }}
                      />
                    ) : (
                      /* CREATE mode: School selection based on user role */
                      <>
                        {/* HEADMASTER: Readonly text input showing their school */}
                        {permissions.role === 'HEADMASTER' ? (
                          <input
                            id="schoolCode"
                            type="text"
                            value={newUser.schoolCode}
                            readOnly
                            disabled
                            className="form-control"
                            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                          />
                        ) : (
                          /* PPD/ADMIN/SUPERADMIN: Dropdown with School Code - Name */
                          <select
                            id="schoolCode"
                            value={newUser.schoolCode}
                            onChange={(e) => setNewUser({ ...newUser, schoolCode: e.target.value })}
                            className="form-control"
                            disabled={!selectedPpdForModal && (permissions.role === 'PPD' || permissions.role === 'ADMIN' || permissions.role === 'SUPERADMIN')}
                          >
                            <option value="">{t('userManagement:modal.fields.school.placeholder')}</option>
                            {schools.map((school) => (
                              <option key={school.schoolCode} value={school.schoolCode}>
                                {school.schoolCode} - {school.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    )}

                    <small className="form-help">
                      {modalMode === 'edit'
                        ? 'School cannot be changed after user creation'
                        : t('userManagement:modal.fields.school.help')}
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4><i className="fas fa-shield-alt"></i> {t('userManagement:modal.sections.permissions')}</h4>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newUser.isActive}
                        onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })}
                      />
                      {t('userManagement:modal.fields.isActive.label')}
                    </label>
                    <small className="form-help">{t('userManagement:modal.fields.isActive.help')}</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                {t('userManagement:buttons.cancel')}
              </button>
              <button
                onClick={handleSaveUser}
                disabled={!newUser.name?.trim() || !newUser.email?.trim() ||
                         (modalMode === 'create' && (!newUser.password || newUser.password.length < 6))}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? t('userManagement:modal.add.title') : t('userManagement:buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Reset Password */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('userManagement:passwordResetModal.title')}</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Error and Success Messages inside Password Reset Modal */}
              {error && <div className="error-message" style={{ marginBottom: '15px' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ marginBottom: '15px' }}>{successMessage}</div>}

              <div className="user-info-box">
                <p><strong>{t('userManagement:table.headers.name')}:</strong> {passwordResetData.userName}</p>
                <p><strong>{t('userManagement:table.headers.email')}:</strong> {passwordResetData.userEmail}</p>
              </div>

              <div className="form-group">
                <label htmlFor="resetPassword">{t('userManagement:passwordResetModal.newPassword.label')} *</label>
                <input
                  id="resetPassword"
                  type="password"
                  value={passwordResetData.newPassword}
                  onChange={(e) => setPasswordResetData({ ...passwordResetData, newPassword: e.target.value })}
                  placeholder={t('userManagement:passwordResetModal.newPassword.placeholder')}
                  minLength={8}
                  className="form-control"
                />
                <small className="form-help">{t('userManagement:passwordResetModal.newPassword.help')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmResetPassword">{t('userManagement:passwordResetModal.confirmPassword.label')} *</label>
                <input
                  id="confirmResetPassword"
                  type="password"
                  value={passwordResetData.confirmPassword}
                  onChange={(e) => setPasswordResetData({ ...passwordResetData, confirmPassword: e.target.value })}
                  placeholder={t('userManagement:passwordResetModal.confirmPassword.placeholder')}
                  minLength={8}
                  className="form-control"
                />
                <small className="form-help">{t('userManagement:passwordResetModal.newPassword.help')}</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="btn btn-secondary"
              >
                {t('userManagement:buttons.cancel')}
              </button>
              <button
                onClick={handleSavePasswordReset}
                disabled={!passwordResetData.newPassword || passwordResetData.newPassword.length < 8}
                className="btn btn-primary"
              >
                {t('userManagement:buttons.resetPassword')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: t('userManagement:title'),
            searchTerm: searchTerm || t('common:none'),
            roleFilter: roleFilter || t('userManagement:filters.allRoles'),
            statusFilter: statusFilter || t('userManagement:filters.allStatuses'),
            districtFilter: districtFilter || t('userManagement:filters.allDistricts'),
            schoolFilter: schoolFilter || t('userManagement:filters.allSchools'),
            totalUsers: totalCount,
            currentUserRole: permissions.role,
            dataScope: permissions.dataScope,
            currentPage: currentPage,
            pageSize: pageSize
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;